import React, { useState, useEffect, useRef, useContext } from 'react';
import './ParkAssistPrologue.scss';
import VideoScroll from '@/v2/components/media/video/VideoScroll';
import Header from '@/v2/components/application/Header';
import ScrollController from '@/v2/components/controllers/ScrollController';
import TimelineText from '@/v2/components/atoms/TimelineText';
import TimeController from '@/v2/components/controllers/TimeController';
import SwipeController from '@/v2/components/controllers/SwipeController';
import { UseSimulatedScrolling } from '@/v2/components/utils/DeviceUtils';
import TextBottom from '@/v2/components/atoms/TextBottom';
import ScrollMouse from '@/v2/components/atoms/Icon/ScrollMouse';
import ScrollSwipe from '@/v2/components/atoms/Icon/ScrollSwipe';
import { AnalyticsContext } from '@/v2/components/application/Analytics';
import Disclaimer from '@/v2/components/layouts/Disclaimer';
import { DriverAssistContext } from '@/v2/state/providers';
import { timeout, saveTimeout, clearTimeouts } from '@/v2/components/utils/Timeout';

const STATE = {
    none: 'none',
    cta: 'cta',
    scrolling: 'scrolling',
    timeline: 'timeline',
    end: 'end',
};

const videoReady = (controller, videoController, swipeController, scrollController, refContainer, onReady, cb, parkAssistPrologueTracking) => {
    // eslint-disable-next-line no-param-reassign
    videoController.current = controller;

    controller.Play(); // Fix later. Not best preloading technique.

    let calledOnce = false;

    // eslint-disable-next-line no-param-reassign
    scrollController.current = new ScrollController(controller.GetEndTime(), 30, {
        fixedUpdate: true,
        manualScrollDirection: UseSimulatedScrolling(),
        onTime: (time) => {
            if (!calledOnce && time > 0.1) {
                calledOnce = true;
                cb();
                parkAssistPrologueTracking();
            }

            controller.SeekTime(time);
        },
    });

    if (UseSimulatedScrolling()) {
        // eslint-disable-next-line no-param-reassign
        swipeController.current = new SwipeController(refContainer.current);

        swipeController.current.onUp(() => {
            scrollController.current.SetScrollDirection(ScrollController.SCROLL_DIRECTION.forward);
        });

        swipeController.current.onDown(() => {
            scrollController.current.SetScrollDirection(ScrollController.SCROLL_DIRECTION.backwards);
        });

        swipeController.current.onLeft(() => {});

        swipeController.current.onRight(() => {});
    }

    onReady();
};

const stateCta = (videoController, scrollController, swipeController) => {
    scrollController.current.Start();
    videoController.current.Pause(); // Fix later. Not best preloading technique.
    videoController.current.SeekTime(0); // Fix later. Not best preloading technique.

    if (UseSimulatedScrolling()) {
        swipeController.current.run();
    }
};

const stateTimeline = (scrollController, timeController, timeline, setTime, cb) => {
    scrollController.current.Stop();

    // eslint-disable-next-line no-param-reassign
    timeController.current = new TimeController(setTime);
    saveTimeout(timeController.current.AddTimeoutFromTimeline(timeline, () => cb()));
};

const parkAssistPrologueTrackEvent = (trackEvent) => {
    const trackingObject = {
        vcStep: 'driverAssist',
        vcClick: 'scroll: park the car',
    };

    trackEvent('vcClick', trackingObject);
};

export default ({ isActive, data, onFinished, onReady }) => {
    const [state, setState] = useState(STATE.none);
    const [scrollMouseAnimate, setScrollMouseAnimate] = useState(true);
    const [scrollTextAnimate, setScrollTextAnimate] = useState('out');

    const videoController = useRef(null);
    const scrollController = useRef(null);
    const timeController = useRef(null);
    const swipeController = useRef(null);

    const refContainer = useRef();
    const time = useRef(TimelineText.Time());
    const { trackEvent } = useContext(AnalyticsContext);
    const [{ disclaimer }] = useContext(DriverAssistContext);

    useEffect(() => {
        if (isActive) {
            setState(STATE.cta);
            setScrollTextAnimate('in');
            timeout(() => {
                setScrollMouseAnimate(false);
            }, 1200);
        }
    }, [isActive]);

    useEffect(() => {
        switch (state) {
            case STATE.cta:
                stateCta(videoController, scrollController, swipeController);
                break;
            case STATE.timeline:
                setScrollTextAnimate('out');
                timeout(() => {
                    stateTimeline(scrollController, timeController, data.text.timeline, time.current.Set, () => setState(STATE.end));
                }, 1200);
                break;
            case STATE.end:
                onFinished();
                break;
            default:
        }
    }, [state]);

    useEffect(
        () => () => {
            if (scrollController.current) {
                scrollController.current.Stop();
            }

            clearTimeouts();
        },
        []
    );

    const options = {
        bufferBeforePlay: true,
        onReady: (controller) =>
            videoReady(
                controller,
                videoController,
                swipeController,
                scrollController,
                refContainer,
                onReady,
                () => setState(STATE.scrolling),
                () => parkAssistPrologueTrackEvent(trackEvent)
            ),
        autoPlay: false,
        minimumBuffer: 1,
        onFinished: () => setState(STATE.timeline),
    };

    const disclaimerContent = disclaimer ? <Disclaimer data={disclaimer} dark /> : '';

    return (
        <div className="ParkAssistPrologue" ref={refContainer}>
            <div className="ParkAssistPrologue__scrollText">
                <TextBottom background="dark" animate={scrollTextAnimate} color="black" component="h4">
                    {data.text.cta.scroll}
                    <i className="Icon__scrollMouse">
                        <ScrollMouse loop={false} isstopped={scrollMouseAnimate} ispaused={scrollMouseAnimate} />
                    </i>
                    <i className="Icon__scrollSwipe">
                        <ScrollSwipe loop={false} isstopped={scrollMouseAnimate} ispaused={scrollMouseAnimate} />
                    </i>
                </TextBottom>
            </div>
            <VideoScroll src={data.video} options={options} />
            <Header show hideLogo dark />
            <TimelineText data={data.text.timeline} time={time.current} background="dark" color="black" />
            {disclaimerContent}
        </div>
    );
};
